// New file — single source of truth for all 16 practitioner type metadata.
// Previously each page (SchedulePage, NewShiftPage, ClinicCalendarTab) declared
// its own local copy of these maps, causing them to drift out of sync whenever a
// new type was added. Centralizing here means adding a 17th type only requires
// editing this file (plus models.py and practitionerFormUtils.js).

// Human-readable label for each normalized type code.
// Used in dropdowns, filter selects, roster tables, and day-detail sidebars.
export const TYPE_LABELS = {
  DDS: "Dentist",
  HYG: "Hygienist",
  PL: "Practice Lead",
  AM: "Area Manager",
  RCP: "Receptionist",
  RC: "Recall Coordinator",
  TC: "Treatment Coordinator",
  DAL1: "Dental Assistant L1",
  DAL2: "Dental Assistant L2",
  HYGR: "Hygienist Restorative",
  NUR: "Nurse",
  RN: "RN",
  FT: "Floater",
  STR: "Sterilization",
  SRG: "Surgeon",
  SPC: "Specialist",
};

// Tailwind bg+text class pair for each type — used in avatar circles and inline badges.
// Strings must be complete (not dynamically constructed) so Tailwind JIT includes
// these classes in the production build even if no component renders them at scan time.
export const TYPE_COLORS = {
  DDS: "bg-palette-cyan-muted text-palette-cyan-heading", // cyan is in palette.cyan (design system); distinct from HYGR blue
  HYG: "bg-palette-emerald-muted text-palette-emerald-hover",
  PL: "bg-palette-orange-muted text-palette-orange-heading", // was amber — swapped with AM so PL/RC (orange/purple) are maximally far apart
  AM: "bg-palette-amber-muted text-palette-amber-heading", // was orange — swapped with PL
  RCP: "bg-palette-sky-muted text-palette-sky-heading",
  RC: "bg-palette-purple-muted text-palette-purple-heading", // was yellow — purple is far from PL orange and RCP sky
  TC: "bg-palette-rose-muted text-palette-rose-heading", // was teal — rose is far from HYG emerald
  DAL1: "bg-palette-yellow-muted text-palette-yellow-heading", // was violet — yellow is far from DDS cyan
  DAL2: "bg-palette-fuchsia-muted text-palette-fuchsia-heading", // was stone — fuchsia is far from FT stone
  HYGR: "bg-palette-blue-muted text-palette-blue-heading", // was green — blue is far from HYG emerald
  NUR: "bg-palette-pink-muted text-palette-pink-heading",
  RN: "bg-palette-violet-muted text-palette-violet-heading", // was blue — violet frees blue for HYGR
  FT: "bg-palette-stone-muted text-palette-stone-heading", // was slate — stone is far from DAL2 fuchsia
  STR: "bg-palette-teal-muted text-palette-teal-heading", // was lime — teal frees lime for SPC
  SRG: "bg-palette-red-muted text-palette-red-heading",
  SPC: "bg-palette-lime-muted text-palette-lime-heading", // was rose — lime is far from SRG red
};

// Plural forms for section headings and filter dropdowns ("Dentists", "Dental Assistants L1").
// Kept separate from TYPE_LABELS so singular and plural forms stay consistent across the app.
export const TYPE_LABELS_PLURAL = {
  DDS: "Dentists",
  HYG: "Hygienists",
  PL: "Practice Leads",
  AM: "Area Managers",
  RCP: "Receptionists",
  RC: "Recall Coordinators",
  TC: "Treatment Coordinators",
  DAL1: "Dental Assistants L1",
  DAL2: "Dental Assistants L2",
  HYGR: "Hygienists Restorative",
  NUR: "Nurses",
  RN: "RNs",
  FT: "Floaters",
  STR: "Sterilization",
  SRG: "Surgeons",
  SPC: "Specialists",
};

// Controls the sort order of types in roster lists and calendar sidebars.
// Clinical roles (DDS, HYG, HYGR, SRG, SPC) are listed first, then admin/support.
// Types not present in this map fall back to 99 and sort to the bottom.
export const TYPE_ORDER = {
  DDS: 0,
  HYG: 1,
  HYGR: 2,
  SRG: 3,
  SPC: 4, // Clinical
  PL: 5,
  AM: 6,
  RCP: 7,
  RC: 8,
  TC: 9, // Admin / coordination
  DAL1: 10,
  DAL2: 11,
  NUR: 12,
  RN: 13,
  FT: 14,
  STR: 15, // Support
};

// Fallback color applied when a type code has no entry in TYPE_COLORS.
export const DEFAULT_TYPE_COLOR = "bg-palette-gray-subtle text-palette-gray-heading";

/** Returns the Tailwind class pair for a normalized type code, falling back to gray. */
export function typeColor(type) {
  return TYPE_COLORS[type] ?? DEFAULT_TYPE_COLOR;
}
